import { z } from "zod";
import {
  ConnectionRequestIdPublicSchema,
  EventParticipantIdPublicSchema,
} from "./branded-id.schemas.js";
import { ParticipantPhotoUrlSchema } from "./participant-photo-url.schemas.js";
import {
  ConnectionRequestDirection,
  ConnectionRequestDirectionSchema,
} from "./connection-request.schemas.js";
import { IntroOriginSchema } from "./intro-origin.schemas.js";
import { EventParticipantStatusSchema } from "./participant-state.schemas.js";
import { IntroRequestStatusSchema } from "./intro-request-state.schemas.js";
import { paginatedResponseSchema } from "./paginated.schemas.js";

/**
 * Query for `GET /api/v1/events/:eventIdPublic/connection-requests`. Not built on
 * `PaginationQuerySchema`: that carries `sortBy`/`sortOrder` and this sort is
 * fixed (newest first).
 */
export const HostConnectionRequestsQuerySchema = z
  .object({
    page: z.coerce.number().int().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    participantIdPublic: EventParticipantIdPublicSchema.optional(),
    direction: ConnectionRequestDirectionSchema.optional(),
    // Only `"true"`: a row is a pair, so `vip=false` has no single meaning —
    // neither side a VIP, or either side not one. This narrows or it is absent.
    vip: z.literal("true").optional(),
  })
  .superRefine((value, ctx) => {
    // A direction is meaningless without a participant to be relative to, `all`
    // included: the field is left optional so the refine sees `undefined` for an
    // omitted param and can reject an explicit one.
    if (
      value.participantIdPublic === undefined &&
      value.direction !== undefined
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["direction"],
        message: "direction requires participantIdPublic",
      });
    }
  })
  // Applied after the refine, so the unfiltered event list still reads as `all`
  // without a field default masking an explicit `?direction=all`.
  .transform((value) => ({
    ...value,
    direction: value.direction ?? ConnectionRequestDirection.all,
  }));

export type HostConnectionRequestsQuery = z.infer<
  typeof HostConnectionRequestsQuerySchema
>;

/**
 * One connection request as an event host reads it. Display fields ride inline so
 * the client is spared a join. Excluded on purpose: `message` (written to one
 * named recipient), `originData` and `statusHistory` (no consumer in this read).
 */
export const HostConnectionRequestRowSchema = z.object({
  idPublic: ConnectionRequestIdPublicSchema,
  status: IntroRequestStatusSchema,
  origin: IntroOriginSchema,
  createdAt: z.coerce.date(),
  // When the request reached its terminal state — accepted, declined or
  // cancelled — so `status` reads as an outcome with a date rather than a
  // standing claim. Null while the request is still live.
  respondedAt: z.coerce.date().nullable(),
  hasNote: z.boolean(),

  requesterParticipantIdPublic: EventParticipantIdPublicSchema,
  requesterName: z.string(),
  requesterTitle: z.string().nullable(),
  requesterPhotoUrl: ParticipantPhotoUrlSchema.nullable(),
  requesterVip: z.boolean(),

  targetParticipantIdPublic: EventParticipantIdPublicSchema,
  targetName: z.string(),
  targetTitle: z.string().nullable(),
  targetPhotoUrl: ParticipantPhotoUrlSchema.nullable(),
  targetVip: z.boolean(),
  // Target only: a request aimed at someone who has not joined cannot be
  // answered, which is a fact about the request the requester's own state is not.
  targetStatus: EventParticipantStatusSchema,
});

export type HostConnectionRequestRow = z.infer<
  typeof HostConnectionRequestRowSchema
>;

export const HostConnectionRequestsSchema = paginatedResponseSchema(
  HostConnectionRequestRowSchema,
);
